import React from 'react';
import { ApiAccess } from '../../../enums/ApiAccess';
import { Checkbox, Icon, Popup } from 'semantic-ui-react';
import { i18n } from '../../../services/i18n';
import './styles.scss';

interface Props {
  apiAccessValue?: ApiAccess[];
  oauthAccessValue: boolean;
  isCreating: boolean;
  isEditing: boolean;
  isUserAdmin: boolean;
  onApiAccessChange?: (value: ApiAccess[]) => void;
  onOauthAccessChange?: (value: boolean) => void;
}

const ConsumerApiAccess: React.FC<Props> = ({
  onApiAccessChange = () => undefined,
  onOauthAccessChange = () => undefined,
  oauthAccessValue,
  apiAccessValue = [],
  isCreating,
  isEditing,
  isUserAdmin,
}) => {
  const accessTypes = [
    { access: ApiAccess.Expenses },
    { access: ApiAccess.Trips },
    { access: ApiAccess.TripsDefault, indented: true },
    { access: ApiAccess.TripsInvoices, indented: true },
    { access: ApiAccess.TripsItinerary, indented: true },
    { access: ApiAccess.TripsMetadata, indented: true },
    { access: ApiAccess.Tickets },
    { access: ApiAccess.FlightAlerts },
    { access: ApiAccess.Receipts },
    { access: ApiAccess.Enh },
    { access: ApiAccess.WaiversMatching },
    { access: ApiAccess.WaiversStandAlone },
    { access: ApiAccess.Xx1 },
  ];

  const oauthUnboundAccessTypes = [
    ApiAccess.TripsDefault,
    ApiAccess.TripsInvoices,
    ApiAccess.TripsItinerary,
    ApiAccess.TripsMetadata,
  ];

  const handleApiAccessChange = (checked: boolean, access: ApiAccess) => {
    const newApiAccessValue = getNewApiAccessValue(checked, access);
    onApiAccessChange(newApiAccessValue);

    if (oauthUnboundAccessTypes.includes(access)) {
      //if trips api format checkbox value is changed
      //do not trigger oauth checkbox change
      return;
    }

    if (newApiAccessValue.filter((x) => !oauthUnboundAccessTypes.includes(x)).length > 0) {
      onOauthAccessChange(true);
    } else {
      onOauthAccessChange(false);
    }
  };

  const handleOauthAccessChange = (checked: boolean) => {
    onOauthAccessChange(!checked);
  };

  const getNewApiAccessValue = (checked: boolean, access: ApiAccess) => {
    if (checked) {
      return access === ApiAccess.WaiversStandAlone
        ? [
            ...apiAccessValue.filter(
              (a) => a !== ApiAccess.WaiversStandAlone && a !== ApiAccess.WaiversMatching,
            ),
          ]
        : [...apiAccessValue.filter((a) => a !== access)];
    } else {
      return access === ApiAccess.WaiversMatching
        ? [...apiAccessValue, ApiAccess.WaiversStandAlone, ApiAccess.WaiversMatching]
        : [...apiAccessValue, access];
    }
  };

  return (
    <div>
      <h4 className="create-consumer-api-title">
        {i18n('ConsumerForm_ApiAccessTitle')}
        {isCreating && !isUserAdmin && (
          <Popup
            offset={[-11]}
            content={i18n('ConsumerForm_Create_AllowedApiAccess_Hint')}
            trigger={<Icon name="info circle" color="grey" />}
          />
        )}
        {isEditing && !isUserAdmin && (
          <Popup
            offset={[-11]}
            content={i18n('ConsumerForm_Edit_AllowedApiAccess_Hint')}
            trigger={<Icon name="info circle" color="grey" />}
          />
        )}
      </h4>
      <div className="create-consumer-api-content">
        {accessTypes.map((t) => {
          return (
            <div key={t.access}>
              <Checkbox
                checked={apiAccessValue.some((v) => v === t.access)}
                className={`checkbox-input${t.indented ? ' indented' : ''}`}
                label={i18n(`ApiAccess_${ApiAccess[t.access]}`)}
                disabled={(!isEditing && !isCreating) || !isUserAdmin}
                onChange={() => {
                  handleApiAccessChange(
                    apiAccessValue.some((v) => v === t.access),
                    t.access,
                  );
                }}
              />
            </div>
          );
        })}
      </div>
      {(!isCreating || isUserAdmin) && (
        <div>
          <h4 className="create-consumer-api-title">{i18n('ConsumerForm_OauthAccessTitle')}</h4>
          <Checkbox
            checked={oauthAccessValue}
            className="checkbox-input"
            disabled={(!isEditing && !isCreating) || !isUserAdmin}
            onChange={() => handleOauthAccessChange(oauthAccessValue)}
          />
        </div>
      )}
    </div>
  );
};

export default ConsumerApiAccess;
